import Money from 'common/Money';
import {ChevronRight} from 'lucide-react';
import * as React from 'react';
import {toDisplayBalance} from 'src/account/balanceDisplay';
import {useUnitsById} from 'src/hooks/useUnits';
import {formatMoneyWithUnits} from 'src/lib/unitDisplay';
import {cn} from 'src/lib/utils';
import type {SectionAccount} from 'src/AccountsView/accountBalances';
import type {UnitsById} from 'src/lib/unitDisplay';

/** An account's contribution to cross-currency totals, in USD cents. */
const homeValueOf = (account: SectionAccount): number | null =>
  account.currency === 'USD'
    ? account.balance
    : (account.homeBalance ?? null);

/**
 * Subtotal of stored balances. Liabilities are stored negative, so a plain
 * sum is honest. A section made only of credit cards totals in the rows' own
 * display sign instead (debt as positive "owed"), so the header agrees with
 * the rows under it; a mixed group keeps the net-worth sign. A
 * single-currency section totals natively; a mixed-currency section totals
 * the USD-converted values instead, marked "≈".
 */
const subtotalLabel = (
  accounts: SectionAccount[],
  unitsById: UnitsById,
): string | null => {
  if (accounts.length === 0) {
    return null;
  }
  // Balances stream in after the list renders — a subtotal over a partial
  // set would understate silently, so show nothing until every row has one.
  const loaded = accounts.flatMap((account) =>
    account.balance === null ? [] : [{...account, balance: account.balance}],
  );
  if (loaded.length !== accounts.length) {
    return null;
  }
  const allCards = loaded.every((account) => account.creditCard);
  const currency = loaded[0].currency;
  if (loaded.every((account) => account.currency === currency)) {
    const amount = loaded.reduce((sum, account) => sum + account.balance, 0);
    return formatMoneyWithUnits(
      Money.fromServer({amount: toDisplayBalance(amount, allCards), currency}),
      unitsById,
    );
  }
  // Mixed currencies total in USD — but only when EVERY account has a home
  // value. Counting an unvalued account as zero (an unrated wallet, or a
  // foreign investment account, which exposes no homeBalance at all) would
  // understate the total while still labelling it a subtotal; showing nothing
  // is the honest option, as it already is for other unknowns.
  const homeValues = loaded.map(homeValueOf);
  if (homeValues.some((value) => value === null)) {
    return null;
  }
  const amount = homeValues.reduce((sum, value) => (sum ?? 0) + (value ?? 0), 0);
  return `≈ ${Money.toDecimalString(
    Money.fromServer({
      amount: toDisplayBalance(amount ?? 0, allCards),
      currency: 'USD',
    }),
  )}`;
};

/** A group's subtotal label, or null while it can't be stated honestly. */
export const useSectionSubtotal = (accounts: SectionAccount[]) =>
  subtotalLabel(accounts, useUnitsById());

/** The rounded, hairline-divided card that holds a group's rows. */
export const SectionCard = ({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => (
  <div
    className={cn(
      'border-border bg-card divide-border flex flex-col divide-y overflow-hidden rounded-xl border',
      className,
    )}
  >
    {children}
  </div>
);

type Props = {
  /** Section title. */
  label: string;
  /** Chips rendered after the label (e.g. the Starred glyph). */
  badges?: React.ReactNode;
  /** Accounts in the section — drives the count and subtotal. */
  accounts: SectionAccount[];
  collapsed: boolean;
  onToggle: () => void;
  /** Rendered before the chevron (group drag handle in rearrange mode). */
  dragHandle?: React.ReactNode;
  /** Section body: the account rows, rendered inside one grouped card. */
  children: React.ReactNode;
};

/**
 * Collapsible section for account lists: a small heading with count and
 * subtotal, and the rows grouped beneath it in one card. When collapsed,
 * the heading and subtotal stay visible.
 */
export const AccountSection = ({
  label,
  badges,
  accounts,
  collapsed,
  onToggle,
  dragHandle,
  children,
}: Props) => {
  const subtotal = useSectionSubtotal(accounts);

  return (
    <section className="flex flex-col gap-1.5">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        onClick={onToggle}
        onKeyDown={(e) => {
          // Only toggle for keys on the header itself — Enter/Space on a
          // nested control (the drag grip) must not collapse the section.
          if (e.target !== e.currentTarget) {
            return;
          }
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        className="group flex min-h-8 cursor-pointer items-center gap-1.5 px-1 select-none"
      >
        {dragHandle && (
          // Clicks on the grip must not toggle the section
          <div onClick={(e) => e.stopPropagation()}>{dragHandle}</div>
        )}
        <ChevronRight
          className={cn(
            'text-muted-foreground size-4 shrink-0 transition-transform duration-100',
            !collapsed && 'rotate-90',
          )}
        />
        {/* leading-5 pins the row's height to the subtotal's line box, so
            the header doesn't grow when subtotals stream in with the
            balances and jitter the list down. */}
        <span className="text-foreground/80 group-hover:text-foreground min-w-0 truncate text-sm leading-5 font-semibold transition-colors">
          {label}
        </span>
        {badges}
        <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
          {accounts.length}
        </span>
        <div className="flex-1" />
        {subtotal != null && (
          // Neutral either way: the sign says which way it goes.
          <span className="text-muted-foreground shrink-0 text-sm leading-5 tabular-nums">
            {subtotal}
          </span>
        )}
      </div>

      {!collapsed && <SectionCard>{children}</SectionCard>}
    </section>
  );
};
